import { useState, useEffect, useMemo, useCallback } from "react";
import { useAttemptedQuestions } from "../context/AttemptedQuestionsContext";
import { decryptData } from "../crypto";
import { useQuestionRecord } from "../context/QuestionRecord";

export const useQuestionNavigatorGate = ({
  questionData,
  currentQuestion,
  activeSectionId,
  activeSubjectId,
}) => {
  const [questions, setQuestions] = useState([]);
  const [answered, setAnswered] = useState(0);
  const [marked, setMarked] = useState(0);
  const [notVisited, setNotVisited] = useState(0);
  const [notAnswered, setNotAnswered] = useState(0);
  const [markedWithAnswered, setMarkedWithAnswered] = useState(0);
  const [visitedQuestions, setVisitedQuestions] = useState([]);

  const { attemptedQuestions } = useAttemptedQuestions();
  const { addQuestionRecord, questionRecords, getQuestionRecordById } =
    useQuestionRecord();

// useEffect(() => {
//   if (!attemptedQuestions || !questions) return;
  
//   attemptedQuestions.forEach((attemptedQuestion) => {
//     // Find the corresponding question in the questions array
//     const foundQuestion = questions.find(
//       (q) => Number(q.question_id) === Number(attemptedQuestion.question_id)
//     );
    
//     if (foundQuestion) {
//       // Merge the objects and add to record
//       addQuestionRecord({
//         ...attemptedQuestion,
//         section_id: foundQuestion.section_id,
//         subject_id: foundQuestion.subject_id
//       });
//     } else {
//       // If no matching question found, just add the original
//       addQuestionRecord(attemptedQuestion);
//     }
//   });
// }, [attemptedQuestions,questions]);

  useEffect(() => {
    if (!attemptedQuestions) return;
    attemptedQuestions?.map((question) => {
      addQuestionRecord(question);
    });
  }, [attemptedQuestions]);



  // Memoize decryptData result to prevent unnecessary changes
  const examTemplate = useMemo(() => {
    return decryptData(sessionStorage.getItem("exam_template"));
  }, []);

  const filteredQuestions = useMemo(() => {
    return (
      questions?.filter(
        (q) =>
          Number(q.section_id) === Number(activeSectionId) &&
          Number(q.subject_id) === Number(activeSubjectId)
      ) || []
    );
  }, [questions, activeSectionId, activeSubjectId]);

  // Initialize questions - runs only once when questionData changes
  useEffect(() => {
    if (!questionData || questions.length > 0) return;

    const initialQuestions = questionData.map((q, idx) => ({
      section_id: q.section_id,
      subject_id: q.subject_id,
      number: idx + 1,
      status: "notVisited",
      question_id: q.id,
    }));
    setQuestions(initialQuestions);
  }, [questionData,questionRecords]); // Only depends on questionData

  // Update first filtered question status - careful with filteredQuestions dependency
  useEffect(() => {
    if (!filteredQuestions.length) return;

    const firstFiltered = filteredQuestions[0];
    setQuestions((prevQuestions) => {
      const shouldUpdate = prevQuestions.some(
        (q) =>
          q.section_id === firstFiltered.section_id &&
          q.subject_id === firstFiltered.subject_id &&
          q.number === firstFiltered.number &&
          q.status === "notVisited"
      );

      if (!shouldUpdate) return prevQuestions;

      return prevQuestions.map((q) =>
        q.section_id === firstFiltered.section_id &&
        q.subject_id === firstFiltered.subject_id &&
        q.number === firstFiltered.number &&
        q.status === "notVisited"
          ? { ...q, status: "notAnswered" }
          : q
      );
    });
  }, [filteredQuestions.length]); // Only depend on length to prevent loops

  // Track visited questions - optimized version
  const updateVisitedQuestions = useCallback(() => {
    if (currentQuestion === undefined || !filteredQuestions.length) return;

    if (currentQuestion!==0) return
    const currentQ = filteredQuestions[currentQuestion];
    if (!currentQ  ) return;

    setVisitedQuestions((prev) => {
      const isAlreadyVisited = prev.some(
        (vq) => vq.question_id === currentQ.question_id
      );
      return isAlreadyVisited
        ? prev
        : [...prev, { question_id: currentQ.question_id }];
    });
    
  }, [currentQuestion,filteredQuestions]);

  useEffect(() => {
    updateVisitedQuestions();
  }, [updateVisitedQuestions]);

  // Stable reference for status update logic
  // if (!filteredQuestions.length || !attemptedQuestions?.length || !questionRecords.length) return;
  const updateQuestionStatuses = useCallback(() => {

    setQuestions((prevQuestions) => {
      let hasChanges = false;
      const newQuestions = prevQuestions.map((q) => {
        const isInFiltered = filteredQuestions.some(
          (fq) => fq.question_id === q.question_id
        );
        if (!isInFiltered) return q;

        const matched = questionRecords.find(
          (aq) => Number(aq.question_id) === Number(q.question_id)
        );

        let newStatus = q.status;
       const wasVisited = visitedQuestions.some(
          (vq) =>{ if(vq.question_id === q.question_id){
            return vq.question_id === q.question_id
          }}
        );

        if (examTemplate !== "default" && wasVisited && !matched) {
          newStatus = "notAnswered";
        }

        if (matched) {
          if (matched.attempt_status === "attempted") {
            newStatus = "attempted";
          }
          if (
            matched.attempt_status === "attempted" &&
            matched.multiple_option_ids?.length === 0
          ) {
            newStatus = "notAnswered";
          }
          if (
            matched.attempt_status === "not answered" &&
            (!matched.option_id ||
              !matched.multiple_option_ids ||
              matched.multiple_option_ids?.length === 0)
          ) {
            newStatus = "notAnswered";
          }
          if (matched.attempt_status === "marked" && matched.option_id) {
            newStatus = "markedWithAnswered";
          }
          if (matched.attempt_status === "marked" && !matched.option_id) {
            newStatus = "marked";
          }
          if (
            matched.attempt_status === "marked" &&
            !matched.option_id &&
            matched.multiple_option_ids?.length > 0
          ) {
            newStatus = "markedWithAnswered";
          }
            if (matched.attempt_status === "marked" && matched.answer) {
            newStatus = "markedWithAnswered";
          }
        }

        if (q.status !== newStatus) {
          hasChanges = true;
          return { ...q, status: newStatus };
        }
        return q;
      });

      return hasChanges ? newQuestions : prevQuestions;
    });
  }, [attemptedQuestions, filteredQuestions, visitedQuestions,questionRecords]);

  
  // Run status updates with proper dependencies
  useEffect(() => {
    updateQuestionStatuses();
  }, [updateQuestionStatuses]);

  // Calculate counts - optimized
  useEffect(() => {
    if (!filteredQuestions.length) return;

    const counts = filteredQuestions.reduce((acc, q) => {
      acc[q.status] = (acc[q.status] || 0) + 1;
      return acc;
    }, {});

    setAnswered(counts.attempted || 0);
    setMarked(counts.marked || 0);
    setMarkedWithAnswered(counts.markedWithAnswered || 0);
    setNotAnswered(counts.notAnswered || 0);
    setNotVisited(
      filteredQuestions.length -
        (counts.attempted || 0) -
        (counts.marked || 0) -
        (counts.markedWithAnswered || 0) -
        (counts.notAnswered || 0)
    );
  }, [filteredQuestions]);

  return {
    questions: filteredQuestions,
    answered,
    marked,
    notVisited,
    notAnswered,
    markedWithAnswered,
    visitedQuestions,
  };
};